/**
 * Deal installation_date — validation when moving to a capture stage
 * (Deal Won / Closed Won / B opció / Partner megkeresés).
 */

import { initSupabaseFromEnv } from "./db";
import { normalizeStageLabel } from "./deal-stage-movement-rules";

export class MissingInstallationDateError extends Error {
  readonly code = "missing_installation_date";

  constructor() {
    super("installation_date is required when moving a deal to a Won stage");
    this.name = "MissingInstallationDateError";
  }
}

export function normalizeInstallationDateInput(raw: unknown): string | null {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  return null;
}

export function getDealInstallationDate(deal: Record<string, unknown>): string | null {
  const raw = deal.installation_date ?? deal.installationDate;
  if (raw == null || raw === "") return null;
  return String(raw).split("T")[0];
}

function stageLabelVariants(stage: Record<string, unknown>): string[] {
  const raw = [stage.name, stage.name_hu, stage.name_sk, stage.name_en];
  const out = new Set<string>();
  for (const v of raw) {
    if (v == null || v === "") continue;
    out.add(String(v));
  }
  return [...out];
}

/**
 * Stages where installation_date must be captured on manual stage move.
 * Intentionally narrower than isWonStageRow (e.g. Megrendelve is not included).
 */
export function isInstallationDateCaptureStageName(name: string): boolean {
  const n = normalizeStageLabel(name);
  if (!n) return false;
  if (n === "deal won" || n === "closed won") return true;
  if (n === "b opcio") return true;
  if (n.includes("partner megkereses") || n.includes("partner inquiry")) return true;
  return false;
}

export function isInstallationDateCaptureStageRow(stage: Record<string, unknown>): boolean {
  return stageLabelVariants(stage).some(isInstallationDateCaptureStageName);
}

async function fetchStageById(
  env: { SUPABASE_URL: string; SUPABASE_ANON_KEY: string },
  stageId: string,
): Promise<Record<string, unknown> | null> {
  const db = await initSupabaseFromEnv(env);
  const { data, error } = await db.from("stages").select("*").eq("id", stageId).maybeSingle();
  if (error || !data) return null;
  return data as Record<string, unknown>;
}

/**
 * Throws MissingInstallationDateError if moving to a capture stage without installation_date on deal or in patch.
 */
export async function assertInstallationDateForWonStageChange(
  env: { SUPABASE_URL: string; SUPABASE_ANON_KEY: string },
  existingDeal: Record<string, unknown>,
  newStageId: string,
  installationDateInput?: unknown,
): Promise<void> {
  const currentStageId = String(existingDeal.stage_id ?? existingDeal.stageId ?? "");
  if (currentStageId === newStageId) return;

  const stage = await fetchStageById(env, newStageId);
  if (!stage || !isInstallationDateCaptureStageRow(stage)) return;

  const existing = getDealInstallationDate(existingDeal);
  const incoming = normalizeInstallationDateInput(installationDateInput);
  if (existing || incoming) return;

  throw new MissingInstallationDateError();
}
